import { Injectable, inject } from '@angular/core';
import { Store } from '@ngrx/store';
import { Observable, Subject, Subscriber, Subscription, filter, switchMap, take } from 'rxjs';
import { WebSocketSubject, webSocket } from 'rxjs/webSocket';

import {
  WebsocketAuthenticateMessage,
  WebsocketAuthenticationResultMessage,
  WebsocketHeartbeatMessage,
  WebsocketHeartbeatResponseMessage,
} from '@core/api/websocket-core.models';
import { connectToWebsocketSuccess, websocketClosed, websocketFailure } from '@core/stores/websocket/websocket.actions';
import { WEBSOCKET_AUTHENTICATION_REJECTED } from '@core/stores/websocket/websocket.constants';
import { selectAuthenticated } from '@core/stores/websocket/websocket.selectors';
import { CONFIG_TOKEN } from '@core/tokens';

import { WebsocketMessageBase } from './websocket.types';

interface WebsocketConnection {
  subject$: WebSocketSubject<unknown>;
  subscription: Subscription;
  // Once discarded, all open/close/error events of this connection's socket are ignored — the
  // rxjs closeObserver fires for deliberately killed sockets too, which must not trigger reconnects
  discarded: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class WebsocketService {
  readonly #store = inject(Store);
  readonly #config = inject(CONFIG_TOKEN);

  private readonly topicObserversMap: Record<string, Subscriber<WebsocketMessageBase>[]> = {};

  private readonly messageSubject$ = new Subject<WebsocketMessageBase>();
  private currentConnection?: WebsocketConnection;

  public connect(): void {
    const previousConnection = this.currentConnection;
    if (previousConnection) {
      previousConnection.discarded = true;
      previousConnection.subscription.unsubscribe();
    }

    const connection = { discarded: false } as WebsocketConnection;

    connection.subject$ = webSocket({
      url: this.#config.backendWebsocketUrl,
      openObserver: {
        next: () => {
          if (!connection.discarded) {
            this.#store.dispatch(connectToWebsocketSuccess());
          }
        },
      },
      closeObserver: {
        next: () => {
          if (!connection.discarded) {
            this.#store.dispatch(websocketClosed());
          }
        },
      },
    });

    connection.subscription = connection.subject$.subscribe({
      next: (message) => {
        this.messageSubject$.next(message as WebsocketMessageBase);
      },
      error: (error) => {
        if (!connection.discarded) {
          console.error('Websocket connection error:', error);
          this.#store.dispatch(websocketFailure({ error: error as string }));
        }
      },
      complete: () => {},
    });

    this.currentConnection = connection;
  }

  public connectToTopic$<T extends WebsocketMessageBase>(topic: string): Observable<T> {
    return this.#store.select(selectAuthenticated).pipe(
      filter((authenticated) => authenticated),
      take(1),
      switchMap(() => {
        return new Observable<T>((observer) => {
          const sub = this.messageSubject$.subscribe({
            next: (message) => {
              if (message.topic === topic) {
                if (message.type === 'subscribe-rejected') {
                  this.removeSubscriberFromTopic(topic, observer);
                  console.error(`Failed to subscribe to topic: ${topic}`);
                  return;
                }
                observer.next(message as T);
              }
            },
            error: (error) => {
              this.removeSubscriberFromTopic(topic, observer);
              observer.error(error);
            },
            complete: () => {
              this.removeSubscriberFromTopic(topic, observer);
              observer.complete();
            },
          });

          this.addNewSubscriberToTopic(topic, observer);

          return () => {
            sub.unsubscribe();
            this.removeSubscriberFromTopic(topic, observer);
          };
        });
      })
    );
  }

  public authenticate$(accessToken: string): Observable<void> {
    return new Observable((observer) => {
      const sub = this.messageSubject$
        .pipe(
          filter((message: unknown): message is WebsocketAuthenticationResultMessage => {
            const typedMessage = message as WebsocketAuthenticationResultMessage;
            return typedMessage.type === 'authentication-result';
          }),
          take(1)
        )
        .subscribe({
          next: (message) => {
            if (message.result === 'ACCEPTED') {
              observer.next();
              observer.complete();
            } else {
              observer.error(WEBSOCKET_AUTHENTICATION_REJECTED);
            }
          },
          error: (error) => {
            observer.error(error);
          },
          complete: () => {
            observer.complete();
          },
        });

      this.send({ type: 'authenticate', jwtToken: accessToken } satisfies WebsocketAuthenticateMessage);

      return () => {
        sub.unsubscribe();
      };
    });
  }

  public sendHeartbeat$(): Observable<void> {
    return new Observable((observer) => {
      const id = crypto.randomUUID();

      const sub = this.messageSubject$
        .pipe(
          filter((message: unknown): message is WebsocketHeartbeatResponseMessage => {
            const typedMessage = message as WebsocketHeartbeatResponseMessage;
            return typedMessage.type === 'heartbeat-response' && typedMessage.id === id;
          }),
          take(1)
        )
        .subscribe(() => {
          observer.next();
          observer.complete();
        });

      this.send({ type: 'heartbeat', id } satisfies WebsocketHeartbeatMessage);

      return () => {
        sub.unsubscribe();
      };
    });
  }

  private addNewSubscriberToTopic(topic: string, Subscriber: Subscriber<WebsocketMessageBase>): void {
    if (!this.topicObserversMap[topic]) {
      this.topicObserversMap[topic] = [];
    }

    this.topicObserversMap[topic].push(Subscriber);

    if (this.topicObserversMap[topic].length === 1) {
      this.send({ type: 'subscribe', topic });
    }
  }

  private removeSubscriberFromTopic(topic: string, subscriber: Subscriber<WebsocketMessageBase>): void {
    if (!this.topicObserversMap[topic]) {
      return;
    }

    this.topicObserversMap[topic] = this.topicObserversMap[topic].filter((sub) => sub !== subscriber);

    if (this.topicObserversMap[topic].length === 0) {
      delete this.topicObserversMap[topic];
      this.send({ type: 'unsubscribe', topic });
    }
  }

  private send(message: unknown): void {
    this.currentConnection?.subject$.next(message);
  }
}
